import { useState } from 'react';
import { Phone, MessageSquare, Mail, FileText, Calendar, ExternalLink, Play, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow, format } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { openRecording } from '@/utils/openRecording';
import { CustomerLink } from '@/components/customers/CustomerLink';
import type { Communication } from '@/types/communications';

interface CommunicationCardProps {
  communication: Communication;
  /**
   * Customer to link the name to. Pass the list-level phone fallback result;
   * defaults to whatever the row itself carries.
   */
  customerId?: string | null;
}

const typeIcons = {
  call: Phone,
  sms: MessageSquare,
  email: Mail,
  note: FileText,
  meeting: Calendar,
};

const typeColors = {
  call: 'bg-info/10 text-info',
  sms: 'bg-success/10 text-success',
  email: 'bg-primary/10 text-primary',
  note: 'bg-warning/10 text-warning',
  meeting: 'bg-destructive/10 text-destructive',
};

export function CommunicationCard({ communication, customerId }: CommunicationCardProps) {
  const [loadingRecording, setLoadingRecording] = useState(false);
  const { toast } = useToast();
  const Icon = typeIcons[communication.type];

  const handleOpenRecording = async () => {
    setLoadingRecording(true);
    const err = await openRecording(communication.id);
    if (err) toast({ title: 'Recording unavailable', description: err, variant: 'destructive' });
    setLoadingRecording(false);
  };
  const colorClass = typeColors[communication.type];

  const formatDuration = (seconds?: number) => {
    if (!seconds) return null;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Card className="hover:bg-muted/50 transition-colors">
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          {/* Type Icon */}
          <div className={`p-2 rounded-full ${colorClass}`}>
            <Icon className="h-4 w-4" />
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Header Row */}
            <div className="flex items-center justify-between gap-2 mb-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium capitalize">{communication.type}</span>
                {communication.direction && (
                  <Badge variant={communication.direction === 'inbound' ? 'success' : 'info'}>
                    {communication.direction === 'inbound' ? '↓ Inbound' : '↑ Outbound'}
                  </Badge>
                )}
                {communication.duration_seconds && (
                  <span className="text-xs text-muted-foreground">
                    {formatDuration(communication.duration_seconds)}
                  </span>
                )}
              </div>
              <span className="text-sm text-muted-foreground whitespace-nowrap" title={format(new Date(communication.timestamp), 'PPpp')}>
                {formatDistanceToNow(new Date(communication.timestamp), { addSuffix: true })}
              </span>
            </div>

            {/* Contact Info */}
            <div className="flex items-center gap-2 text-sm mb-1">
              <CustomerLink
                customerId={customerId ?? communication.customer_id}
                name={communication.customer_name}
                className="font-medium"
              />
              <span className="text-muted-foreground">•</span>
              <span className="text-muted-foreground">{communication.customer_phone}</span>
            </div>

            {/* Lead/Job Reference */}
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
              {communication.lead_number && (
                <Link
                  to={`/leads/${communication.lead_id}`}
                  className="text-primary hover:underline"
                >
                  {communication.lead_number}
                </Link>
              )}
              {communication.campaign_name && (
                <>
                  <span>•</span>
                  <span>{communication.campaign_name}</span>
                </>
              )}
              {communication.tracking_label && (
                <>
                  <span>•</span>
                  <span title="CTM tracking label">{communication.tracking_label}</span>
                </>
              )}
              {communication.agent_name && (
                <>
                  <span>•</span>
                  <span>Handled by: {communication.agent_name}</span>
                </>
              )}
            </div>

            {/* Content Preview */}
            {communication.content && (
              <p className="text-sm text-muted-foreground line-clamp-2">
                {communication.subject && <strong>{communication.subject}: </strong>}
                {communication.content}
              </p>
            )}

            {/* Outcome */}
            {communication.outcome && (
              <p className="text-sm text-muted-foreground mt-1">
                <span className="font-medium">Outcome:</span> {communication.outcome}
              </p>
            )}
          </div>
        </div>

        {/* Actions Row */}
        <div className="flex items-center gap-2 mt-3 ml-12">
          {communication.type === 'call' && (communication.talk_time_seconds || 0) > 0 && (
            <Button variant="outline" size="sm" onClick={handleOpenRecording} disabled={loadingRecording}>
              {loadingRecording
                ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Loading...</>
                : <><Play className="h-3 w-3 mr-1" />Play Recording</>
              }
            </Button>
          )}

          {communication.lead_id && (
            <Button variant="outline" size="sm" asChild>
              <Link to={`/leads/${communication.lead_id}`}>
                <ExternalLink className="h-3 w-3 mr-1" />
                View Lead
              </Link>
            </Button>
          )}
        </div>

        {/* Inline Recording Player */}
      </CardContent>
    </Card>
  );
}
